import { openDB, DBSchema, IDBPDatabase } from 'idb';
import type { PendingAudit, Store, Segment } from '@/types';

// Schema do IndexedDB
interface AuditDBSchema extends DBSchema {
    'pending-audits': {
        key: string;
        value: PendingAudit;
        indexes: { 'by-store': string; 'by-status': string };
    };
    'cached-stores': {
        key: string;
        value: Store;
    };
    'cached-segments': {
        key: string;
        value: Segment;
    };
    'sync-queue': {
        key: string;
        value: {
            id: string;
            type: 'audit' | 'measurement' | 'backlight';
            data: unknown;
            createdAt: number;
            retryCount: number;
        };
    };
}

const DB_NAME = 'shelf-audit-db';
const DB_VERSION = 1;

let dbInstance: IDBPDatabase<AuditDBSchema> | null = null;

/**
 * Obter instância do banco
 */
async function getDB(): Promise<IDBPDatabase<AuditDBSchema>> {
    if (dbInstance) return dbInstance;

    dbInstance = await openDB<AuditDBSchema>(DB_NAME, DB_VERSION, {
        upgrade(db) {
            // Store para auditorias pendentes
            if (!db.objectStoreNames.contains('pending-audits')) {
                const auditStore = db.createObjectStore('pending-audits', { keyPath: 'id' });
                auditStore.createIndex('by-store', 'storeId');
                auditStore.createIndex('by-status', 'syncStatus');
            }

            // Store para lojas em cache
            if (!db.objectStoreNames.contains('cached-stores')) {
                db.createObjectStore('cached-stores', { keyPath: 'id' });
            }

            // Store para segmentos em cache
            if (!db.objectStoreNames.contains('cached-segments')) {
                db.createObjectStore('cached-segments', { keyPath: 'id' });
            }

            // Store para fila de sincronização
            if (!db.objectStoreNames.contains('sync-queue')) {
                db.createObjectStore('sync-queue', { keyPath: 'id' });
            }
        },
    });

    return dbInstance;
}

// ============ Pending Audits ============

/**
 * Salvar auditoria pendente
 */
export async function saveAudit(audit: PendingAudit): Promise<void> {
    const db = await getDB();
    await db.put('pending-audits', audit);
}

/**
 * Obter auditoria pendente
 */
export async function getAudit(id: string): Promise<PendingAudit | undefined> {
    const db = await getDB();
    return db.get('pending-audits', id);
}

/**
 * Obter todas as auditorias pendentes
 */
export async function getAllPendingAudits(): Promise<PendingAudit[]> {
    const db = await getDB();
    return db.getAll('pending-audits');
}

/**
 * Obter auditorias pendentes por status
 */
export async function getPendingAuditsByStatus(status: PendingAudit['syncStatus']): Promise<PendingAudit[]> {
    const db = await getDB();
    return db.getAllFromIndex('pending-audits', 'by-status', status);
}

/**
 * Atualizar status de sincronização
 */
export async function updateAuditSyncStatus(
    id: string,
    status: PendingAudit['syncStatus'],
    error?: string
): Promise<void> {
    const db = await getDB();
    const audit = await db.get('pending-audits', id);
    if (audit) {
        audit.syncStatus = status;
        if (error) {
            audit.syncError = error;
        }
        await db.put('pending-audits', audit);
    }
}

/**
 * Deletar auditoria pendente
 */
export async function deleteAudit(id: string): Promise<void> {
    const db = await getDB();
    await db.delete('pending-audits', id);
}

/**
 * Contar auditorias pendentes
 */
export async function countPendingAudits(): Promise<number> {
    const db = await getDB();
    return db.count('pending-audits');
}

// ============ Cached Stores ============

/**
 * Salvar lojas em cache
 */
export async function cacheStores(stores: Store[]): Promise<void> {
    const db = await getDB();
    const tx = db.transaction('cached-stores', 'readwrite');
    await Promise.all([
        ...stores.map(store => tx.store.put(store)),
        tx.done,
    ]);
}

/**
 * Obter lojas do cache
 */
export async function getCachedStores(): Promise<Store[]> {
    const db = await getDB();
    return db.getAll('cached-stores');
}

/**
 * Obter loja do cache
 */
export async function getCachedStore(id: string): Promise<Store | undefined> {
    const db = await getDB();
    return db.get('cached-stores', id);
}

// ============ Cached Segments ============

/**
 * Salvar segmentos em cache
 */
export async function cacheSegments(segments: Segment[]): Promise<void> {
    const db = await getDB();
    const tx = db.transaction('cached-segments', 'readwrite');
    await Promise.all([
        ...segments.map(segment => tx.store.put(segment)),
        tx.done,
    ]);
}

/**
 * Obter segmentos do cache
 */
export async function getCachedSegments(): Promise<Segment[]> {
    const db = await getDB();
    return db.getAll('cached-segments');
}

// ============ Sync Queue ============

/**
 * Adicionar item à fila de sincronização
 */
export async function addToSyncQueue(
    type: 'audit' | 'measurement' | 'backlight',
    data: unknown
): Promise<string> {
    const db = await getDB();
    const id = `sync-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    await db.put('sync-queue', {
        id,
        type,
        data,
        createdAt: Date.now(),
        retryCount: 0,
    });
    return id;
}

/**
 * Obter itens da fila de sincronização
 */
export async function getSyncQueue(): Promise<{ id: string; type: string; data: unknown; retryCount: number }[]> {
    const db = await getDB();
    return db.getAll('sync-queue');
}

/**
 * Remover item da fila
 */
export async function removeFromSyncQueue(id: string): Promise<void> {
    const db = await getDB();
    await db.delete('sync-queue', id);
}

/**
 * Incrementar contagem de retry
 */
export async function incrementRetryCount(id: string): Promise<void> {
    const db = await getDB();
    const item = await db.get('sync-queue', id);
    if (item) {
        item.retryCount += 1;
        await db.put('sync-queue', item);
    }
}

/**
 * Limpar todos os dados (para debug/reset)
 */
export async function clearAllData(): Promise<void> {
    const db = await getDB();
    await Promise.all([
        db.clear('pending-audits'),
        db.clear('cached-stores'),
        db.clear('cached-segments'),
        db.clear('sync-queue'),
    ]);
}
